<template>
  <div class="invite-page">
    <TopBarClose
      title="친구 초대"
      back-url="/"
    />
    <ImageMessageBlock
      image="/img/event/invite-friend.png"
      image-size="288"
    >
      <template #title>
        <div class="text-center">
          친구가 1,000원을<br />
          보냈어요!
        </div>
      </template>
      <template #sub-title>
        <div class="text-center">
          내가 회원가입하면<br />
          친구도 적립금 500원을 받아요.
        </div>
        <p class="desc text-center">* 이 페이지에서 가입해야 적립금이 지급돼요.</p>
      </template>
      <template #content>
        <InviteNote
          note-type="receive"
          class="content"
        />
      </template>
    </ImageMessageBlock>
    <div class="buttons w-full flex gap-8 p-16">
      <CtaButton
        id="gtm_invite_get_btn"
        size="lg"
        w-full
        text="데이터 적립금 받기"
        @click-button="handleGetPointButton"
      />
    </div>

    <RewardResultModal
      :visible="rewardResultModalVisible"
      :api-status="rewardApiStatus"
      :reward-result-type="rewardResultType"
      :on-complete="handleGoPoint"
      :on-go-home="handleGoHome"
      @close="rewardResultModalVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: 'center'
})

useHead({
  meta: [{ property: 'og:image:secure_url', content: 'https://www.usimsa.com/img/invite-og.png' }]
})

const route = useRoute()
const { ogUrl } = useSeo()
const config = useRuntimeConfig()
const { isLoginStatus } = storeToRefs(useAuthStore())
const { referralApi } = useApi()
const { message } = useAlarm()

const referralCode = route.query.referral as string | undefined
const rewardResultModalVisible = ref(false)
const rewardApiStatus = ref<'success' | 'fail'>()
const rewardResultType = ref<ReferralRewardResult>()

useSeoMeta({
  title: '유심사 가입하고 선물 받기',
  description:
    '친구 초대로 유심사에 가입하면 웰컴 적립금 선물과 해외 데이터 eSIM 할인을 드려요. 여행 준비, 지금 빠르게 시작해요!',
  ogTitle: '친구가 선물한 1,000원, 지금 받기!',
  ogDescription: '내가 가입하면 친구도 적립금을 받아요.',
  ogImage: config.public.baseUrl + '/img/invite-og.png',
  ogImageWidth: '1200',
  ogImageHeight: '630',
  ogImageType: 'image/png',
  ogType: 'website',
  ogUrl: () => config.public.baseUrl + route.fullPath
})

const handleGetPointButton = async () => {
  if (!referralCode) {
    message('info', '초대한 친구 코드가 잘못되었어요.', false, true, 2000, 90, 'message-plain')
    return
  }

  if (!isLoginStatus.value) {
    navigateTo(
      `/user/login?returnUrl=${encodeURIComponent(route.fullPath)}&referral=${referralCode}`
    )
    return
  }

  try {
    const res = await referralApi.patchReferralReward({
      type: 'SIGNUP',
      referralCode: referralCode ?? ''
    })

    if (!res?.success) {
      throw new Error('Failed to get referral reward')
    } else {
      rewardApiStatus.value = 'success'
      rewardResultType.value = res.data?.result as ReferralRewardResult
      rewardResultModalVisible.value = true
    }
  } catch (error) {
    console.error('Error getting referral reward:', error)
    rewardApiStatus.value = 'fail'
    rewardResultModalVisible.value = true
  }
}

const handleGoPoint = () => {
  navigateTo('/user/point', {
    replace: true
  })
}

const handleGoHome = () => {
  navigateTo('/', {
    replace: true
  })
}
</script>

<style lang="scss" scoped>
.invite-page {
  .desc {
    @include footnote2-medium;
    color: $rose-500;
    margin-top: 20px;
  }
  .buttons {
    position: fixed;
    max-width: 500px;
    bottom: 0;
    background: $base-white;
  }
}
</style>
